import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { toSearchParams } from '@/shared/lib/search';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchCountriesList, type Country } from './api';
import { CountryModal } from './CountryModal';

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id' },
  { value: 'name', label: 'Поиск по стране' },
];

const emptyFilters = { searchKey: '', searchValue: '' };

/** `null` — closed, `{}` — create, `{ country }` — edit. */
type ModalState = { country?: Country } | null;

export const CountriesPage = () => {
  const list = useListFilters(emptyFilters);
  const { draft, applied, page, sortField, sortOrder } = list;
  const [modal, setModal] = useState<ModalState>(null);

  const params = {
    page,
    sort_field: sortField,
    sort_order: sortOrder,
    ...toSearchParams(applied.searchKey, applied.searchValue),
  };
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['place-countries', params],
    queryFn: () => fetchCountriesList(params),
  });
  const rows = getListRows<Country>(data, ['countries']);

  return (
    <>
      <section className="content-header">
        <h1>Справочник мест</h1>
        <div className="row">
          <div className="col-sm-12">
            <button type="button" className="btn btn-info pull-right" onClick={() => setModal({})}>
              Создать
            </button>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Страна</h3>
            <form onSubmit={list.submit} style={{ marginTop: 12 }}>
              <FilterField label="Поиск">
                <SearchKeyInput
                  keyOptions={SEARCH_KEY_OPTIONS}
                  searchKey={draft.searchKey}
                  searchValue={draft.searchValue}
                  onKeyChange={(v) => list.setField('searchKey', v)}
                  onValueChange={(v) => list.setField('searchValue', v)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки стран" />
            <table className="table table-bordered">
              <thead>
                <tr>
                  <SortableTh active={sortField === 'id'} onClick={() => list.toggleSort('id')}>
                    id
                  </SortableTh>
                  <SortableTh active={sortField === 'name'} onClick={() => list.toggleSort('name')}>
                    Страна
                  </SortableTh>
                  <th>Трехсимвольный код страны</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((country) => (
                  <tr key={country.id}>
                    <td>{country.id}</td>
                    <td>{country.name}</td>
                    <td>{country.phone_code}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => setModal({ country })}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
          </div>
        </div>
      </section>
      {modal && (
        <CountryModal key={modal.country?.id ?? 'new'} country={modal.country} onClose={() => setModal(null)} />
      )}
    </>
  );
};
